// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Snowflake, PenLine, Truck, Search, Package, Printer, AlertTriangle, Plus, XCircle, Building2 } from 'lucide-react'
import { getRxSiteOfCare, type SiteOfCareBlock } from '../../api/siteOfCare'
import { shipmentAlert } from '../../lib/scanSounds'
import * as api from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { scanGood, scanWarn } from '../../lib/scanSounds'
import { RELABEL_REASONS, RELABEL_GROUPS } from '../../lib/relabelReasons'
import { formatDob, formatPhone } from '../../api/format'
import type { ServiceOptionsInput } from '../../api/shipping'
import { useCarrierServices } from '../../hooks/queries/shipping'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import PickupLocationField from '../../components/shipping/PickupLocationField'
import { carrierSetup } from '../../lib/carriers'

/**
 * Create-shipment drawer (Sprint 18). One entry point, two modes:
 *   - "From prescription": search/pick any Rx (any state — flexible/
 *     pre-dispense); address auto-fills from the patient. State only advances
 *     server-side if the Rx was genuinely dispensed.
 *   - "Manual package": type the recipient address; no Rx link.
 * Same carrier picker, handling toggles, quote-then-confirm as the Rx flow.
 */
export default function CreateShipmentDrawer({ onClose, onCreated, initialRxId, initialMoreRxIds, fullScreen }: { onClose: () => void; onCreated: () => void ; initialRxId?: string; initialMoreRxIds?: string[]; fullScreen?: boolean }) {
  const navigate = useNavigate()
  const [mode, setMode] = useState<'rx' | 'manual' | 'facility'>('rx')
  // D-SHIP-FAC-1 (Patrick 2026-09-05): "Facility" is the Rx flow with ONE rule swapped — the
  // patient gate becomes an address gate (one package per facility, many residents inside).
  const isRxMode = mode === 'rx' || mode === 'facility'
  // D-SHIP-17 (Patrick): on Fridays default the service to SATURDAY delivery so a
  // Friday package is scheduled for Saturday. Pharmacy-local day (not UTC) — a
  // Friday-evening UTC shipment must not read as Saturday. Default only; the
  // pharmacist can still change the dropdown, and their choice always wins.
  const isFridayLocal = () => new Date().getDay() === 5
  const [serviceType, setServiceType] = useState<string>('')
  const [serviceUserSet, setServiceUserSet] = useState(false)   // once the user picks, stop auto-defaulting
  // D-SHIP-16 (Patrick): print the label as soon as Create confirms (order number
  // returns synchronously via the ASP-90 flow). Default ON; uncheck to create
  // without printing.
  const [printAfterCreate, setPrintAfterCreate] = useState(true)
  const [printBlocked, setPrintBlocked] = useState<string | null>(null)   // shipment id whose tab the browser refused
  // D-SHIP-COD-MANUAL: a manual package has no Rx and therefore no claim, so the copay engine
  // cannot price it and the courier could never be told to collect on it. Typing the amount is
  // the only possible source for this shape of order. Same permission as the manager copay
  // override (mig 327) — this is money a driver will demand at a door, not a shipping action.
  const canSetCod = useAuth().hasPermission('copay.override')
  const [codAmount, setCodAmount] = useState('')
  const [carriers, setCarriers] = useState<api.ShippingCarrier[]>([])
  const [carrierId, setCarrierId] = useState('')
  // D-SHIP-23 §10: a branch picked for this order; otherwise the account's default branch.
  const [pickupChoice, setPickupChoice] = useState<{ carrierId: string; id: string } | null>(null)

  // AUTOPRINT FIX (2026-08-04): the print tab MUST be opened synchronously inside the
  // click gesture. Opening it after `await api.createShipment(...)` (a courier round
  // trip, often seconds) spends the browser's transient user activation, so Chrome
  // either blocks the popup outright or opens it as a BACKGROUND tab — and a hidden
  // tab never runs requestAnimationFrame, which is what LabelPrintView's auto-print
  // was waiting on. That is the "a tab opened but I still have to press Print" report.
  //
  // So: open a blank foreground tab on the gesture, park a "Preparing label…" note in
  // it, then point it at the label once the shipment id exists (or close it if the
  // create failed). If the browser refused even this, surface a manual Print link
  // instead of silently doing nothing.
  const openPrintTab = (): Window | null => {
    if (!printAfterCreate) return null
    try {
      const w = window.open('', '_blank')
      if (w) w.document.write('<!doctype html><title>Preparing label…</title>' +
        '<p style="font:14px system-ui,sans-serif;padding:24px;color:#334">Preparing label…</p>')
      return w
    } catch { return null }
  }
  const sendPrintTab = (w: Window | null, shipmentId: string | null | undefined) => {
    if (!printAfterCreate || !shipmentId) { try { w?.close() } catch { /* ignore */ }; return }
    const url = `/shipments/${shipmentId}/label?autoclose=1`
    if (w && !w.closed) { try { w.location.replace(url); return } catch { /* fall through */ } }
    // Popup refused (or lost): try once more — this still runs close to the gesture —
    // and if that fails too, tell the user instead of leaving them with no label.
    let retry: Window | null = null
    try { retry = window.open(url, '_blank') } catch { /* ignore */ }
    if (!retry) setPrintBlocked(shipmentId)
  }

  // Rx mode
  const [rxQuery, setRxQuery] = useState('')
  const rxInputRef = useRef<HTMLInputElement | null>(null)
  const scanInputRef = useRef<HTMLInputElement | null>(null)   // ASP-SHIP-B: 'scan another' field
  const [rxResults, setRxResults] = useState<api.RxSearchRow[]>([])
  const [rxSearching, setRxSearching] = useState(false)
  const [selectedRx, setSelectedRx] = useState<api.RxSearchRow | null>(null)
  const [activeShipments, setActiveShipments] = useState<api.ActiveShipment[]>([])
  const [activeDismissed, setActiveDismissed] = useState(false)
  // D-SHIP-17 combine (Patrick 2026-08-04): "Add this Rx and reprint". This is
  // exactly a multi-Rx shipment built from two live-proven paths: cancel the old
  // order (existing endpoint — reaches e-Courier), then create ONE new shipment
  // containing the old order's Rx + the currently-selected Rx, and print the one
  // combined label. No new void-a-purchased-label logic. Order matters: cancel
  // FIRST so we never hold two live labels for the shared Rx; if cancel is refused
  // (a driver already has it) we abort and change nothing.
  const [combiningId, setCombiningId] = useState<string | null>(null)
  const combineIntoNew = async (sh: api.ActiveShipment) => {
    if (combiningId || !selectedRx || !carrierId) return
    const oldMemberIds = sh.rx.map(x => x.rx_item_id).filter((x): x is string => !!x)
    if (oldMemberIds.length === 0) {
      setError('Could not read the existing order\u2019s prescriptions to combine. Use Ship separately or Cancel instead.')
      return
    }
    setCombiningId(sh.shipment_id); setError(null)
    const printWin = openPrintTab()   // must happen on the gesture, before any await
    try {
      // 1) Cancel the old order first (reaches e-Courier). If refused, abort.
      await api.cancelShipment(sh.shipment_id)
      setActiveShipments(list => list.filter(x => x.shipment_id !== sh.shipment_id))
    } catch (e: any) {
      try { printWin?.close() } catch { /* ignore */ }
      setError(e?.body?.message || e?.message || 'Could not cancel the existing order \u2014 it may already be with a driver. Ship separately instead.')
      setCombiningId(null)
      return
    }
    try {
      // 2) Create ONE new shipment with the old members + the new Rx (dedup).
      const body = buildBody()
      const seen = new Set<string>()
      const merged: NonNullable<api.CreateShipmentInput['rx_items']> = []
      for (const m of [...(body.rx_items ?? []), ...oldMemberIds.map(id => ({ rx_item_id: id }))]) {
        if (m.rx_item_id && !seen.has(m.rx_item_id)) { seen.add(m.rx_item_id); merged.push(m) }
      }
      const r: any = await api.createShipment({ ...body, rx_items: merged, rx_item_id: selectedRx.id })
      setConfirmed({ id: r?.id ?? null, order_number: r?.order_number ?? null, pending: !!r?.order_number_pending })
      sendPrintTab(printWin, r?.status === 'label_purchased' ? r?.id : null)
    } catch (e: any) {
      try { printWin?.close() } catch { /* ignore */ }
      const b = e?.body ?? {}
      setError(b?.message || e?.message || 'The old order was cancelled, but creating the combined shipment failed. The prescriptions are unshipped \u2014 create the shipment again.')
    } finally {
      setCombiningId(null)
    }
  }
  // D-SHIP-17 Option 1 (Patrick 2026-08-04): COMBINE INTO EXISTING ORDER. Add this
  // Rx to the already-boxed order and reprint ONE label under the ORIGINAL order
  // number. No cancel, no new order number, no rate change, no e-Courier call. Backend
  // add-rx inserts the junction member + advances state; then we reprint the SAME
  // order's label (which already lists every member).
  const combineInPlace = async (sh: api.ActiveShipment) => {
    if (combiningId || !selectedRx) return
    setCombiningId(sh.shipment_id); setError(null)
    const printWin = openPrintTab()   // on the gesture, before the await
    try {
      // Every Rx in this package, not just the primary: anything already scanned into
      // pkgItems used to be dropped silently here, leaving that medication unshipped
      // and untracked while the operator saw a success. add-rx takes one Rx per call
      // and is idempotent, so this is a plain sequential add.
      const members = [
        { id: selectedRx.id, code: primaryScanCode || undefined },
        ...pkgItems.map(i => ({ id: i.rx_item_id, code: i.scanned_code })),
      ]
      const failed: string[] = []
      for (const m of members) {
        try {
          await api.addRxToShipment(sh.shipment_id, { rx_item_id: m.id, scanned_code: m.code })
        } catch (inner: any) {
          // The primary failing aborts the whole combine; a secondary failing must not
          // hide the ones that did land, so name it and carry on.
          if (m.id === selectedRx.id) throw inner
          failed.push(pkgItems.find(i => i.rx_item_id === m.id)?.rx_number || m.id)
        }
      }
      if (failed.length > 0)
        setError(`Added to order ${sh.order_number ?? ''}, but these could not be added and are still unshipped: ${failed.join(', ')}.`)
      // Reprint the existing order's label (now showing every Rx). Confirm with the
      // original order number so the same post-print options appear.
      setConfirmed({ id: sh.shipment_id, order_number: sh.order_number ?? null, pending: false })
      setActiveShipments(list => list.filter(x => x.shipment_id !== sh.shipment_id))
      sendPrintTab(printWin, sh.shipment_id)
    } catch (e: any) {
      try { printWin?.close() } catch { /* ignore */ }
      const b = e?.body ?? {}
      setError(b?.message || e?.message || 'Could not add this Rx to the existing order. Ship separately or cancel instead.')
    } finally {
      setCombiningId(null)
    }
  }
  const [primaryScanCode, setPrimaryScanCode] = useState<string | null>(null)
  // D-SHIP-1 bulk: additional Rx scanned into this package (selectedRx = primary).
  const [pkgItems, setPkgItems] = useState<{ rx_item_id: string; rx_number: string; drug_name: string | null; patient_name?: string | null; copay?: number | null; collected?: boolean; mismatch_override?: boolean; override_reason?: string; scanned_code?: string }[]>([])
  const [scanQ, setScanQ] = useState('')
  const [scanBusy, setScanBusy] = useState(false)
  const [scanNote, setScanNote] = useState<string | null>(null)
  type Mismatch = { rx_item_id: string; rx_number: string; drug_name: string | null; actual_patient_name: string | null; mismatch_kind?: 'patient' | 'address'; expected_facility_name?: string | null; actual_facility_name?: string | null }
  const [mismatch, setMismatch] = useState<Mismatch | null>(null)
  // Mismatches pending review (multi-select can produce several) — surfaced one at a time.
  const [mismatchQueue, setMismatchQueue] = useState<Mismatch[]>([])
  // D-SHIP-FAC-1: the facility the PRIMARY Rx ships to (fixes the package's address gate).
  const [facSoc, setFacSoc] = useState<SiteOfCareBlock | null>(null)
  const [facSocErr, setFacSocErr] = useState<string | null>(null)
  const scanKind = mode === 'facility' ? 'facility' as const : undefined
  useEffect(() => {
    setFacSoc(null); setFacSocErr(null)
    if (mode !== 'facility' || !selectedRx) return
    let dead = false
    getRxSiteOfCare(selectedRx.id).then(b => { if (!dead) setFacSoc(b.site_of_care ?? { facility_type: 'residential', facility_type_label: 'Residential', facility_id: null, facility_name: null, facility_phone: null, facility_address: null, match_method: 'none', matched_address: null, resolved_at: null }) }).catch(e => { if (!dead) setFacSocErr(e?.message || 'Could not resolve the facility.') })
    return () => { dead = true }
  }, [mode, selectedRx?.id])
  const [overrideReason, setOverrideReason] = useState('')
  // Full patient context (identity + address) for the selected Rx.
  const [ctx, setCtx] = useState<api.ShipContext | null>(null)
  const [ctxErr, setCtxErr] = useState<string | null>(null)
  const [editAddr, setEditAddr] = useState(false)
  const [addr, setAddr] = useState({ line1: '', city: '', state: '', zip: '', phone: '' })
  const [addToList, setAddToList] = useState(false)
  const [addrLabel, setAddrLabel] = useState('')
  // Scan-box autocomplete
  const [scanSug, setScanSug] = useState<api.RxSearchRow[]>([])

  // Manual mode
  // D-SHIP-PU: explicit pickup / drop-off selectors (manual mode)
  const [pickupType, setPickupType] = useState<'pharmacy' | 'patient' | 'other'>('pharmacy')
  const [dropType, setDropType] = useState<'patient' | 'pharmacy' | 'other'>('patient')
  const [puName, setPuName] = useState('')
  const [puLine1, setPuLine1] = useState('')
  const [puCity, setPuCity] = useState('')
  const [puState, setPuState] = useState('')
  const [puZip, setPuZip] = useState('')
  const [puPhone, setPuPhone] = useState('')
  const [puPatQ, setPuPatQ] = useState('')
  const [puPatResults, setPuPatResults] = useState<api.PatientListItem[]>([])
  const [puPatSel, setPuPatSel] = useState<{ id: string; name: string } | null>(null)
  const [dropPatQ, setDropPatQ] = useState('')
  const [dropPatResults, setDropPatResults] = useState<api.PatientListItem[]>([])
  const [dropPatSel, setDropPatSel] = useState<{ id: string; name: string } | null>(null)
  const patTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 86cb8uk5f: a pending debounced patient search must not fire after the drawer closes.
  useEffect(() => () => { if (patTimer.current) clearTimeout(patTimer.current) }, [])
  const [pharmAddr, setPharmAddr] = useState<any>(null)
  // D-SHIP-13: purchased-label relabel override
  const [relabelPrompt, setRelabelPrompt] = useState<string | null>(null)
  const [relabelOrder, setRelabelOrder] = useState<string | null>(null)
  const [relabelCode, setRelabelCode] = useState('')
  const [relabelText, setRelabelText] = useState('')
  useEffect(() => {
    let live = true
    api.getShippingPharmacyAddress(carrierId || undefined)
      .then(r => { if (live) setPharmAddr(r?.source ? r : null) })
      .catch(() => { if (live) setPharmAddr(null) })
    return () => { live = false }
  }, [carrierId])
  const [recipient, setRecipient] = useState('')
  const [line1, setLine1] = useState('')
  const [city, setCity] = useState('')
  const [stateCode, setStateCode] = useState('')
  const [zip, setZip] = useState('')
  const [phone, setPhone] = useState('')
  const [description, setDescription] = useState('')

  // common
  const [requireSig, setRequireSig] = useState(false)
  const [sigAutoNote, setSigAutoNote] = useState(false)   // ASP-SHIP-A: why signature is pre-checked
  const [coldChain, setColdChain] = useState(false)
  const [adultSig, setAdultSig] = useState(false)
  // Special handling flag — persisted in the saturday_delivery column (repurposed; no migration).
  const [specialHandling, setSpecialHandling] = useState(false)
  const [instructions, setInstructions] = useState('')
  // `key` is the exact order the rate was for; any later change makes it stale (D-SHIP-23).
  const [quote, setQuote] = useState<{ amount: number | null; detail: string; key: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [fullExpand, setFullExpand] = useState(false)   // in-place full screen (P0: never a new tab); declared above the focus effect that depends on it
  // ASP-SHIP-B (Patrick 2026-07-30): land the cursor in the Prescription box when
  // the drawer opens in "From prescription" mode, so staff can scan/type without
  // reaching for the mouse. Only when the search input is actually shown (rx mode,
  // nothing selected yet) — never steals focus in manual mode or after a pick.
  useEffect(() => {
    if (!isRxMode) return
    // ASP-SHIP-B: keep the cursor where the next scan goes. Before a pick, that's
    // the search box; once an Rx is selected, the "scan another Rx" field appears
    // and becomes the target so continuous scanning never needs the mouse.
    const id = requestAnimationFrame(() => {
      if (selectedRx) scanInputRef.current?.focus()
      else rxInputRef.current?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [mode, selectedRx, fullExpand])   // D-SHIP-9: entering Full screen re-lands the cursor in the scan field

  useEffect(() => {
    api.listShippingCarriers().then(res => {
      const active = res.items.filter(c => c.is_active)
      setCarriers(active)
      if (active.length === 1) {
        setCarrierId(active[0].id)
        setColdChain(active[0].default_cold_chain)
        setRequireSig(active[0].default_signature)
      }
    }).catch(() => setError('Could not load carriers.'))
  }, [])

  // ASP-97 (Patrick 2026-07-29): scan → select in ONE step. Wedge scanners type
  // the barcode (x013740500) and send Enter; a busy pharmacist should never have
  // to search-then-click. resolveScan hits scan-check (which decodes the x-prefix,
  // ASP-94) and auto-selects the resolved Rx. Fired by Enter AND automatically
  // when the text matches the scanner pattern, in case a wedge omits Enter.
  const scanPattern = /^[xX]\d{6,}$/
  const resolveScan = async (raw: string): Promise<boolean> => {
    const t = raw.trim()
    if (!t) return false
    setRxSearching(true)
    try {
      const r = await api.shipmentScanCheck(t)
      if (r.verdict !== 'not_found' && r.rx_item_id) {
        setPrimaryScanCode(t)   // D-SHIP-15: the barcode/typed value that resolved this Rx
        setSelectedRx({
          id: r.rx_item_id,
          rx_number: r.rx_number || t,
          drug_name: r.drug_name ?? null,
          patient_name: r.patient_name ?? '',
          state: r.state || '',
        })
        setRxResults([]); setRxQuery('')
        if (r.verdict === 'duplicate') {
          scanWarn()   // D-SHIP-10
          const ord = (r as any).existing_order_number
          setRelabelOrder(ord ?? null)
          setRelabelPrompt(ord
            ? `A label (order ${ord}) was already purchased for this Rx.`
            : 'A label was already purchased for this Rx.')
        }
        return true
      }
    } catch { /* fall through to text search */ } finally { setRxSearching(false) }
    return false
  }

  // debounced Rx search (scan pattern short-circuits into one-step select)
  useEffect(() => {
    if (!isRxMode || rxQuery.trim().length < 2) { setRxResults([]); return }
    const t = setTimeout(async () => {
      if (scanPattern.test(rxQuery.trim())) { await resolveScan(rxQuery); return }
      setRxSearching(true)
      try {
        const res = await api.searchRx(rxQuery.trim())
        setRxResults(res.items.slice(0, 8))
      } catch { /* ignore */ } finally { setRxSearching(false) }
    }, 300)
    return () => clearTimeout(t)
  }, [rxQuery, mode])

  // Bootstrap from a quick-ship deep link (?ship_rx=…)
  useEffect(() => {
    if (!initialRxId) return
    api.getShipContext(initialRxId).then(c => {
      setMode('rx')
      setSelectedRx({ id: c.rx.id, rx_number: c.rx.rx_number, drug_name: c.rx.drug_name, patient_name: c.patient.display_name, state: c.rx.state || '' })
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRxId])

  // Load full patient context whenever the primary Rx changes.
  useEffect(() => {
    if (!selectedRx) {
      setCtx(null); setCtxErr(null); setEditAddr(false); setActiveShipments([])
      // D-SHIP-13: no Rx selected means no reship gate to answer.
      setRelabelPrompt(null); setRelabelOrder(null); setRelabelCode(''); setRelabelText('')
      return
    }
    // D-SHIP-13 follow-up (Patrick 2026-08-27): the reship gate must fire for EVERY way an Rx
    // enters this drawer, not just the scan box. Three paths exist — scan (resolveScan), opened
    // from the Rx list/detail with initialRxId, and picked from the search dropdown — and only
    // the first ran a duplicate check, so an Rx opened from a list showed no reason panel and
    // 'Create shipment' stayed enabled until the server rejected it. This effect is the one
    // place all three converge. scan-check by id runs the IDENTICAL server-side verdict gate as
    // a hand scan (ASP-60), fill-aware, so there is no second definition of "already labeled".
    let cancelled = false
    api.shipmentScanCheckById(selectedRx.id).then(v => {
      if (cancelled) return
      if (v.verdict === 'duplicate') {
        const ord = v.existing_order_number ?? null
        setRelabelOrder(ord)
        setRelabelPrompt(ord
          ? `A label (order ${ord}) was already purchased for this Rx.`
          : 'A label was already purchased for this Rx.')
      } else {
        // This Rx is clean: drop any gate left over from the previous selection. Safe because
        // this verdict IS the rule that raises the gate — the two can never disagree.
        setRelabelPrompt(null); setRelabelOrder(null); setRelabelCode(''); setRelabelText('')
      }
    }).catch(() => { /* verdict unavailable — the server still enforces on create */ })

    setCtxErr(null)
    api.getShipContext(selectedRx.id).then(c => {
      setCtx(c)
      setAddr({ line1: c.patient.address_line ?? '', city: c.patient.city ?? '', state: c.patient.state_code ?? '', zip: c.patient.postal_code ?? '', phone: c.patient.primary_phone ?? '' })
      // ASP-SHIP-A: high-value med (acquisition cost > $200) auto-suggests a
      // signature so it isn't left at the door. A suggestion, not a lock — staff
      // can still uncheck it. The note explains why it's on.
      if (c.rx.require_signature_suggested) { setRequireSig(true); setSigAutoNote(true) }
      else setSigAutoNote(false)
    }).catch((e: unknown) => {
      setCtx(null)
      const msg = e instanceof Error ? e.message : String(e)
      setCtxErr(msg || 'unknown error')
    })
    // A stale verdict must never land on a newer Rx.
    return () => { cancelled = true }
  }, [selectedRx?.id])

  // Scan-box autocomplete: suggest matching Rx while typing (Enter still validates).
  useEffect(() => {
    if (!selectedRx || scanQ.trim().length < 2) { setScanSug([]); return }
    const t = setTimeout(async () => {
      try {
        const res = await api.searchRx(scanQ.trim())
        setScanSug(res.items.filter(r => r.id !== selectedRx.id && !pkgItems.some(i => i.rx_item_id === r.id)).slice(0, 6))
      } catch { /* ignore */ }
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanQ, selectedRx?.id, pkgItems.length])

  const selected = carriers.find(c => c.id === carrierId)
  const requireQuote = selected?.require_quote_confirm !== false

  // ASP-60: multi-select rows arrive as ids; each is admitted through the SAME shipmentScanCheck
  // gate as a hand scan (patient match / duplicate / shippable) — never blind-added.
  const [preloadNote, setPreloadNote] = useState<string | null>(null)
  useEffect(() => {
    if (!selectedRx || !initialMoreRxIds || initialMoreRxIds.length === 0) return
    let dead = false
    ;(async () => {
      const skipped: string[] = []
      for (const rid of initialMoreRxIds) {
        if (dead || rid === selectedRx.id) continue
        try {
          const r = await api.shipmentScanCheckById(rid, selectedRx.id, scanKind)
          if (dead) return
          if (r.verdict === 'ok' && r.rx_item_id && !pkgItemsRef.current.some(i => i.rx_item_id === r.rx_item_id)) {
            setPkgItems(items => [...items, { rx_item_id: r.rx_item_id!, rx_number: r.rx_number || rid, drug_name: r.drug_name ?? null, patient_name: r.patient_name ?? null, copay: r.copay ?? null, collected: r.collected ?? false }])
          } else if (r.verdict === 'mismatch' && r.rx_item_id) {
            // RULE (Patrick 2026-07-23): one shipment = one patient. A different-patient Rx is
            // never silently dropped and never silently added — it queues for the SAME error +
            // audited-override dialog the scan box uses.
            scanWarn()   // D-SHIP-10: preloaded Rx belongs to a different patient
            setMismatchQueue(q => [...q, { rx_item_id: r.rx_item_id!, rx_number: r.rx_number || rid, drug_name: r.drug_name ?? null, actual_patient_name: r.actual_patient_name ?? null, mismatch_kind: r.mismatch_kind, expected_facility_name: r.expected_facility_name, actual_facility_name: r.actual_facility_name }])
          } else if (r.verdict !== 'ok') {
            // duplicate / not-shippable are HARD rules — not overridable.
            skipped.push(`${r.rx_number || rid} (${r.verdict === 'duplicate' ? 'already shipped' : 'not shippable'})`)
          }
        } catch { skipped.push(rid) }
      }
      if (!dead && skipped.length) setPreloadNote(`Skipped: ${skipped.join(', ')}`)
    })()
    return () => { dead = true }
  }, [selectedRx?.id, initialMoreRxIds?.join(',')])
  useEffect(() => {
    if (!mismatch && mismatchQueue.length > 0) {
      setMismatch(mismatchQueue[0]); setMismatchQueue(q => q.slice(1))
    }
  }, [mismatch, mismatchQueue])
  const pkgItemsRef = React.useRef(pkgItems)
  useEffect(() => { pkgItemsRef.current = pkgItems }, [pkgItems])

  // Scan/enter an Rx number → server-side check → add | warn | reject (D-SHIP-1).
  const onScan = async (explicit?: string) => {
    const num = (explicit ?? scanQ).trim()
    if (!num || !selectedRx || scanBusy) return
    setScanBusy(true); setScanNote(null)
    try {
      const r = await api.shipmentScanCheck(num, selectedRx.id, scanKind)
      if (r.verdict === 'ok') {
        if (r.rx_item_id === selectedRx.id || pkgItems.some(i => i.rx_item_id === r.rx_item_id)) {
          scanWarn()   // D-SHIP-10: audible cue — duplicate in package
          setScanNote(`Rx ${r.rx_number} is already in this package.`)
        } else {
          scanGood()   // D-SHIP-10: audible cue — accepted
          setPkgItems(items => [...items, { rx_item_id: r.rx_item_id!, rx_number: r.rx_number || num, drug_name: r.drug_name ?? null, patient_name: r.patient_name ?? null, copay: r.copay ?? null, collected: r.collected ?? false, scanned_code: num }])
          // 86cb27r8q (Yousef via Andrew): the signature suggestion must fire for ANY high-value
          // item in the package, not just the primary. It was computed only from the ship-context
          // of the FIRST Rx, so scanning Opzelura last left the package unsigned. OR it in — and
          // never turn it OFF here: a cheap item scanned afterwards must not clear a signature the
          // package already earned (or the bug simply moves to the other end of the scan order).
          if (r.require_signature_suggested) { setRequireSig(true); setSigAutoNote(true) }
        }
        setScanQ('')
      } else if (r.verdict === 'mismatch') {
        scanWarn()   // D-SHIP-10: wrong patient — the loud one that matters
        setMismatch({ rx_item_id: r.rx_item_id!, rx_number: r.rx_number || num, drug_name: r.drug_name ?? null, actual_patient_name: r.actual_patient_name ?? null, mismatch_kind: r.mismatch_kind, expected_facility_name: r.expected_facility_name, actual_facility_name: r.actual_facility_name })
      } else if (r.verdict === 'not_facility') {
        scanWarn()
        setScanNote('The first Rx ships to a home address — a facility package must start from a facility Rx.')
      } else if (r.verdict === 'duplicate') {
        scanWarn()
        const ord = (r as any).existing_order_number
        setRelabelOrder(ord ?? null)
        setRelabelPrompt(ord
          ? `A label (order ${ord}) was already purchased for Rx ${r.rx_number || num}.`
          : `A label was already purchased for Rx ${r.rx_number || num}.`)
      } else if (r.verdict === 'not_shippable') {
        scanWarn()
        setScanNote(`Rx ${r.rx_number || num} is ${r.state} — not shippable.`)
      } else {
        scanWarn()
        setScanNote(`No Rx found with number "${num}".`)
      }
    } catch { scanWarn(); setScanNote('Scan check failed — try again.') } finally {
      setScanBusy(false)
      // ASP-SHIP-B: return focus to the scan field for the next barcode. rAF so
      // it lands after React re-enables the input (disabled flips off on setScanBusy).
      requestAnimationFrame(() => scanInputRef.current?.focus())
    }
  }

  // D-SHIP-23: the service list comes from the selected carrier. Carriers that price by address
  // (RoadPas) answer for this destination with availability and price; a manual courier has none.
  const destination: ServiceOptionsInput['ship_to'] | null = mode === 'manual'
    ? (dropType !== 'pharmacy' && line1 && city && stateCode && zip ? { line1, city, state: stateCode, zip } : null)
    : (mode === 'rx' && addr.line1 && addr.city && addr.state && addr.zip ? { line1: addr.line1, city: addr.city, state: addr.state, zip: addr.zip } : null)
  const destinationJson = useDebouncedValue(JSON.stringify(destination), 500)
  const servicesShipTo = useMemo(() => JSON.parse(destinationJson) as ServiceOptionsInput['ship_to'] | null, [destinationJson])
  const pickupsFromCarrier = !!selected && carrierSetup(selected.carrier).pickupFromCarrier
  const pickupLocationId = pickupsFromCarrier
    ? (pickupChoice?.carrierId === carrierId ? pickupChoice.id : selected?.account_number ?? '') : ''
  const services = useCarrierServices(carrierId || undefined, {
    ship_to: servicesShipTo ?? undefined, require_signature: requireSig, adult_signature: adultSig, cold_chain: coldChain,
    pickup_location_id: pickupLocationId || undefined,
  })
  const serviceOptions = services.data?.options ?? []
  const offered = serviceOptions.filter(o => o.eligible !== false)
  const showService = !!carrierId && (serviceOptions.length > 0 || !!services.data?.address_aware || services.isLoading)
  useEffect(() => {
    if (!services.data) return
    if (serviceUserSet && offered.some(o => o.code === serviceType)) return
    const saturday = isFridayLocal() && offered.some(o => o.code === 'SATURDAY') ? 'SATURDAY' : null
    const preferred = offered.find(o => o.code === services.data.default_code)?.code
    setServiceType(saturday ?? preferred ?? offered[0]?.code ?? services.data.default_code ?? '')
    if (serviceUserSet && !offered.some(o => o.code === serviceType)) setServiceUserSet(false)
  }, [services.data])   // re-default only when the carrier's answer changes

  const buildBody = (): api.CreateShipmentInput => ({
    carrier_account_id: carrierId,
    service_level: serviceType || undefined,
    pickup_location_id: pickupLocationId || undefined,
    shipment_kind: mode === 'facility' ? 'facility' : undefined,
    // D-SOC-6: record WHICH facility the package went to (mig 428/405's facility_id), so a
    // delivery can be queried against the directory instead of string-matched on its address.
    // In Facility mode that is the facility the Rx already resolves to; the operator is not
    // choosing it, the Rx's site of care is. Kept out of the other modes here because nothing
    // in them picks a facility on this branch.
    ...(mode === 'facility' && facSoc?.facility_id ? { facility_id: facSoc.facility_id } : {}),
    rx_item_id: isRxMode ? selectedRx?.id : undefined,
    rx_items: isRxMode && selectedRx
      ? [{ rx_item_id: selectedRx.id, scanned_code: primaryScanCode || undefined },
         ...pkgItems.map(i => ({ rx_item_id: i.rx_item_id, mismatch_override: i.mismatch_override, override_reason: i.override_reason, scanned_code: i.scanned_code }))]
      : undefined,
    ...(mode === 'rx' && editAddr && addr.line1 && addr.city && addr.zip ? {
      line1: addr.line1, city: addr.city, state: addr.state, zip: addr.zip, phone: addr.phone || undefined,
      add_to_address_list: addToList,
      address_label: addToList && addrLabel.trim() ? addrLabel.trim() : undefined,
    } : {}),
    ...(relabelCode ? { allow_relabel: true, relabel_reason_code: relabelCode, relabel_reason_text: relabelText || undefined } : {}),
    require_signature: requireSig, adult_signature: adultSig,
    cold_chain: coldChain, saturday_delivery: specialHandling,
    special_instructions: instructions || undefined,
    description: description || undefined,
    ...(mode === 'manual' ? {
      pickup_type: pickupType, dropoff_type: dropType,
      ...(dropType === 'patient' && dropPatSel ? { patient_id: dropPatSel.id } : {}),
      ...(pickupType === 'patient' && puPatSel ? { pickup_patient_id: puPatSel.id } : {}),
      ...(dropType !== 'pharmacy' ? { recipient_name: recipient, line1, city, state: stateCode, zip, phone } : {}),
      ...(pickupType !== 'pharmacy' ? {
        pickup_name: puName, pickup_line1: puLine1, pickup_city: puCity,
        pickup_state: puState, pickup_zip: puZip, pickup_phone: puPhone,
      } : {}),
      // Omitted entirely when blank — the server treats 0 as a mistake, and the way to send no
      // COD is to not send the field.
      ...(canSetCod && codAmount.trim() && Number(codAmount) > 0 ? { cod_amount: Number(codAmount) } : {}),
    } : {}),
  })

  const orderKey = JSON.stringify(buildBody())

  const searchPatients = (q: string, side: 'pickup' | 'drop') => {
    if (side === 'pickup') { setPuPatQ(q); setPuPatSel(null) } else { setDropPatQ(q); setDropPatSel(null) }
    if (patTimer.current) clearTimeout(patTimer.current)
    if (q.trim().length < 2) { side === 'pickup' ? setPuPatResults([]) : setDropPatResults([]); return }
    patTimer.current = setTimeout(async () => {
      try {
        const r = await api.listPatients(q.trim())
        const items = (r.items || []).slice(0, 6)
        side === 'pickup' ? setPuPatResults(items) : setDropPatResults(items)
      } catch { /* search miss is non-fatal */ }
    }, 300)
  }
  const pickPatient = async (row: api.PatientListItem, side: 'pickup' | 'drop') => {
    try {
      const full: any = await api.getPatient(row.id)
      const pr = full?.patient || {}
      const name = String(pr.display_name || row.display_name || '')
      const l1 = String(pr.address_line || '')
      const ci = String(pr.city || row.city || '')
      const st = String(pr.state_code || row.state_code || '')
      const zp = String(pr.zip_code || '')
      const ph = String(pr.primary_phone || row.primary_phone || '')
      if (side === 'pickup') {
        setPuPatSel({ id: row.id, name }); setPuPatResults([]); setPuPatQ(name)
        setPuName(name); setPuLine1(l1); setPuCity(ci); setPuState(st); setPuZip(zp); setPuPhone(ph)
      } else {
        setDropPatSel({ id: row.id, name }); setDropPatResults([]); setDropPatQ(name)
        setRecipient(name); setLine1(l1); setCity(ci); setStateCode(st); setZip(zp); setPhone(ph)
      }
    } catch { /* fall back to manual entry of fields */ }
  }

  const pharmacyAddrLine = pharmAddr?.line1
    ? `${pharmAddr.name || 'Pharmacy'} — ${pharmAddr.line1}, ${pharmAddr.city || ''} ${pharmAddr.state || ''} ${pharmAddr.zip || ''}`
    : null
  const pickupOk = pickupType === 'pharmacy' ? !!pharmacyAddrLine : !!(puName && puLine1 && puCity && puZip)
  const dropOk = dropType === 'pharmacy' ? !!pharmacyAddrLine : !!(recipient && line1 && city && zip)
  // D-SHIP-13: a reship reason is part of "is this order creatable", not an afterthought.
  // relabelPrompt is set the moment scan-check returns the duplicate verdict (or the server
  // rejects a create), and until a reason is picked the order cannot be created at all.
  const relabelReady = !relabelPrompt || !!(relabelCode && (relabelCode !== 'EXT99' || relabelText.trim()))
  // Server column limit (ShipmentInputLimits): presets and "Add to instructions" append past the
  // box's own maxLength, so the total is checked here and on the server before the courier call.
  const INSTRUCTIONS_MAX = 512
  const instructionsTooLong = instructions.length > INSTRUCTIONS_MAX
  const canSubmit = !instructionsTooLong && carrierId && (isRxMode ? (!!selectedRx && (mode !== 'facility' || !!facSoc?.facility_id)) : (pickupOk && dropOk)) && relabelReady

  const getQuote = async () => {
    if (!canSubmit) return
    const quotedKey = orderKey
    setBusy(true); setError(null)
    try {
      // quote uses the carrier + (for rx) the patient address resolved server-side;
      // for manual, pass the typed address through the quote ship_to.
      const q = await api.quoteShipment({
        carrier_account_id: carrierId,
        require_signature: requireSig, adult_signature: adultSig,
        cold_chain: coldChain, saturday_delivery: specialHandling,
        service_level: serviceType || undefined,
        pickup_location_id: pickupLocationId || undefined,
        ship_to: mode === 'manual'
          ? { name: recipient, line1, city, state: stateCode, zip, phone }
          : destination ? { ...destination, phone: addr.phone || undefined } : undefined,
        reference: isRxMode ? (selectedRx?.rx_number ?? undefined) : undefined,
      })
      setQuote({ amount: q.amount, detail: q.detail, key: quotedKey })
    } catch (e: any) {
      setError(e?.body?.detail || e?.payload?.detail || e?.message || 'Quote failed.')
    } finally { setBusy(false) }
  }

  const [confirmed, setConfirmed] = useState<{ id?: string | null; order_number: string | null; pending: boolean } | null>(null)
  const [refreshBusy, setRefreshBusy] = useState(false)
  // ASP-90 "try refresh" (Patrick): one reconcile pass; label printing stays blocked until a number lands.
  const refreshOrder = async () => {
    if (!confirmed?.id || refreshBusy) return
    setRefreshBusy(true)
    try {
      const r = await api.confirmShipmentOrder(confirmed.id)
      if (r.confirmed && r.order_number) setConfirmed({ id: confirmed.id, order_number: r.order_number, pending: false })
    } catch { /* stay pending */ } finally { setRefreshBusy(false) }
  }
  // A pending order is reconciled server-side by a 1-minute sweep, but the drawer used
  // to sit there until someone pressed Refresh — so a real, already-numbered order kept
  // reading as pending and could not be printed or combined. Poll the same endpoint the
  // button uses for ~40s, then fall back to the button.
  useEffect(() => {
    if (!confirmed?.pending || !confirmed.id) return
    let tries = 0
    const h = window.setInterval(() => {
      if (document.hidden) return
      if (++tries > 13) { window.clearInterval(h); return }
      void refreshOrder()
    }, 3000)
    return () => window.clearInterval(h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmed?.pending, confirmed?.id])

  // D-SHIP-17 near-real-time: the active-shipment panel used to be a ONE-SHOT fetch
  // fired when the Rx was picked. An order created seconds earlier at another station,
  // or one still reconciling its courier order number, stayed invisible until the
  // operator re-picked the Rx — which is why an order looked un-batchable long after
  // it was ready. Poll while the drawer is open, and re-check the moment the tab
  // regains focus. Paused when the tab is hidden and once this order is confirmed.
  const ACTIVE_POLL_MS = 5000
  const activePatientId = ctx?.patient?.id ?? null
  // D-SHIP-FAC-1: in Facility mode the open-package question is asked of the FACILITY, so a
  // different resident's Rx still finds today's box to Gardena and can be combined into it.
  const activeKey = mode === 'facility' ? (facSoc?.facility_id ?? null) : activePatientId
  const knownActiveRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!activeKey || confirmed) return
    let alive = true
    knownActiveRef.current = new Set()
    const tick = async (first: boolean) => {
      try {
        const res = mode === 'facility' ? await api.activeShipmentsForFacility(activeKey) : await api.activeShipmentsForPatient(activeKey)
        if (!alive) return
        const open = res.shipments || []
        setActiveShipments(open)
        // Alert once per order we have not seen yet — including one that appears
        // mid-poll because another station just created it.
        const fresh = open.filter(s => !knownActiveRef.current.has(s.shipment_id))
        open.forEach(s => knownActiveRef.current.add(s.shipment_id))
        if (fresh.length > 0) {
          if (first) setActiveDismissed(false)
          try { shipmentAlert() } catch { /* fail-silent */ }
        }
      } catch { if (alive && first) setActiveShipments([]) }
    }
    void tick(true)
    const h = window.setInterval(() => { if (!document.hidden) void tick(false) }, ACTIVE_POLL_MS)
    const onFocus = () => { void tick(false) }
    window.addEventListener('focus', onFocus)
    return () => { alive = false; window.clearInterval(h); window.removeEventListener('focus', onFocus) }
  }, [activeKey, mode, confirmed])
  // ASP-88: per-pharmacy quick-text presets for special instructions.
  const [presets, setPresets] = useState<string[]>([])
  useEffect(() => {
    api.getLabelConfig().then(r => setPresets(r.config.instruction_presets ?? [])).catch(() => { /* optional */ })
  }, [])
  const togglePreset = (t: string) => setInstructions(prev => {
    const parts = prev.split(/\s*;\s*/).map(x => x.trim()).filter(Boolean)
    const has = parts.includes(t)
    const next = has ? parts.filter(x => x !== t) : [...parts, t]
    return next.join('; ')
  })
  // A claim exists (copay is a number, possibly 0) vs no claim data at all (null/undefined).
  // D-SHIP-COPAY-MULTI: the per-Rx copay panel (primary + scanned members, each with a
  // collected/pending chip and the driver-collect total) is built inline where it renders.
  const copayKnown = ctx?.copay != null || pkgItems.some(i => i.copay != null)
  const [lastOrder, setLastOrder] = useState<string | null>(null)   // chip after 'Ship another'

  // Ops loop: clear everything package-specific; KEEP the stickies a batch shares —
  // mode, carrier, service level, cold-chain/signature defaults, and full-screen state.
  const resetForNext = () => {
    setLastOrder(confirmed?.order_number ?? null)
    setConfirmed(null); setError(null); setQuote(null); setPrintBlocked(null)
    setSelectedRx(null); setRxQuery(''); setRxResults([])
    setPkgItems([]); setScanQ(''); setScanNote(null); setScanSug([])
    setMismatch(null); setMismatchQueue([]); setOverrideReason(''); setPreloadNote(null)
    setCtx(null); setCtxErr(null); setEditAddr(false)
    setAddr({ line1: '', city: '', state: '', zip: '', phone: '' }); setAddToList(false); setAddrLabel('')
    setRecipient(''); setLine1(''); setCity(''); setStateCode(''); setZip(''); setPhone(''); setDescription('')
    setCodAmount('')   // never let one package's COD leak onto the next
    setInstructions('')
    // D-SHIP-13: one package's reship reason must never carry onto the next Rx.
    setRelabelPrompt(null); setRelabelOrder(null); setRelabelCode(''); setRelabelText('')
  }
  const submit = async () => {
    if (!canSubmit) return
    setBusy(true); setError(null)
    const printWin = openPrintTab()   // on the gesture, before the courier round trip
    try {
      const r: any = await api.createShipment(buildBody())
      // Patrick's flow: creation is only confirmed WITH the courier order number.
      setConfirmed({ id: r?.id ?? null, order_number: r?.order_number ?? null, pending: !!r?.order_number_pending })
      // D-SHIP-16 (Patrick): print-on-create. The ASP-90 flow returns the order
      // number synchronously — status 'label_purchased' means the label is ready.
      // When the box is checked, open the existing label auto-print route (the same
      // one the list's printer icon uses). A pending shipment (no order number) is
      // NEVER printed — LabelPrintView itself enforces that (ASP-90), and we gate on
      // the confirmed status here too. Never blocks the create flow.
      sendPrintTab(printWin, r?.status === 'label_purchased' ? r?.id : null)
      // Ops loop (Patrick 2026-07-23): NO forced auto-close — the confirmation shows
      // explicit choices ('Ship another package' / 'View shipments') so the operations
      // team can keep one tab open and ship package after package.
    } catch (e: any) {
      try { printWin?.close() } catch { /* ignore */ }
      // D-SHIP-13: a purchased label is the ONLY status block — surface the reason
      // picker instead of a dead error. Every other status never lands here.
      const body = e?.body ?? e?.payload ?? {}
      if (body?.error === 'label_already_purchased' || body?.error === 'relabel_reason_required') {
        // Server saw the duplicate first (scan-check can be bypassed by a row-selected Rx).
        // Surface the SAME in-drawer reason panel rather than a popup, and say plainly that
        // nothing was created — canSubmit now blocks until the reason is filled in.
        scanWarn()
        setRelabelOrder(body?.existing_order_number ?? null)
        setRelabelPrompt(body?.message || 'A label was already purchased for this Rx.')
        setError('Not created — a reship reason is required. Pick one above, then create.')
        setBusy(false)
        return
      }
      const rxList: string[] | undefined = body?.rx_numbers
      const base = body?.message || body?.detail || e?.message || 'Create failed.'
      setError(rxList?.length ? `${base} (${rxList.join(', ')})` : base)
    } finally { setBusy(false) }
  }

  // The rate is only good for the order it was quoted on: carrier, service, destination, items,
  // handling, COD. Anything else on screen differs -> show no rate and require a new one.
  const liveQuote = quote && quote.key === orderKey ? quote : null

  return (
    <div style={(fullScreen || fullExpand) ? fullPage : overlay} onClick={(fullScreen || fullExpand) ? undefined : onClose}>
      <div style={(fullScreen || fullExpand) ? drawerFull : drawer} onClick={e => e.stopPropagation()}>
        {fullScreen && (
          <button type="button" title="Close"
            onClick={() => { onClose(); try { window.close() } catch { /* not an opened tab — onClose already returned to the list */ } }}
            style={{ float: 'right', border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
            ✕ Close
          </button>
        )}
        {/* P0 fix (Patrick 2026-07-23): full screen expands IN PLACE. The previous
            window.open booted a fresh app WITHOUT the acting-pharmacy context, so an
            unbound SaaS-admin session rendered its default ADMIN shell around the page —
            tenant scoping held server-side (verified), but a pharmacy flow must NEVER
            jump shells. No new tab, no new app boot, no context to lose. */}
        <button type="button" title={fullExpand ? 'Exit full screen' : 'Expand to full screen'}
          onClick={() => setFullExpand(v => !v)}
          style={{ float: 'right', border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>
          {fullExpand ? '⤡ Exit full screen' : '⧉ Full screen'}
        </button>
        {preloadNote && <div style={{ fontSize: 12, color: 'var(--color-warning, #b45309)', margin: '4px 0 8px' }}>{preloadNote}</div>}
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Create shipment</h3>

        {confirmed?.order_number && (
          <div style={{ background: '#f0fdf4', border: '1px solid #16a34a', borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 10 }}>
            ✓ Order <b>{confirmed.order_number}</b> confirmed with the courier — tracking is live, label ready to print.
            {/* The browser refused the print tab (popup blocker). Never leave the
                pharmacist with a confirmed order and no way to reach its label. */}
            {printBlocked && (
              <div style={{ marginTop: 8, background: '#fffbeb', border: '1px solid #d97706', borderRadius: 6, padding: '7px 9px', color: '#92400e', fontSize: 12.5 }}>
                Your browser blocked the print window. <a href={`/shipments/${printBlocked}/label`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, color: '#92400e' }}>Open the label</a> — or allow pop-ups for this site so it prints automatically.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={resetForNext}
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-accent, #1a56db)', color: '#fff', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                📦 Create another order
              </button>
              <button type="button" onClick={() => { onClose(); navigate(`/shipments?shipment=${confirmed.id}`) }}
                style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                Review order
              </button>
              <button type="button" onClick={() => { onClose(); navigate('/shipments') }}
                style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                Go to shipment dashboard
              </button>
            </div>
          </div>
        )}
        {!confirmed && lastOrder && (
          <div style={{ fontSize: 12, color: 'var(--color-text-mute, #6b7280)', marginBottom: 8 }}>
            Last order: <b>{lastOrder}</b> ✓
          </div>
        )}
        {confirmed?.pending && (
          <div style={{ background: '#fffbeb', border: '1px solid #d97706', borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 10 }}>
            ⚠ Order sent to the courier but <b>no order number is confirmed yet</b>. It is saved as pending and will
            reconcile automatically. <b>Label printing is blocked until the courier assigns an order number</b> — use
            Refresh to check for it.
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button type="button" onClick={() => void refreshOrder()} disabled={refreshBusy}
                style={{ border: '1px solid #d97706', background: '#d97706', color: '#fff', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {refreshBusy ? 'Checking…' : '↻ Refresh order number'}
              </button>
              <button type="button" onClick={resetForNext}
                style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                📦 Ship another package
              </button>
              <button type="button" onClick={onCreated}
                style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                View shipments
              </button>
            </div>
          </div>
        )}
        {/* D-SHIP-13 (Patrick 2026-08-27): the reship reason lives IN the drawer, not in a
            popup over it. The rule is the server's: a label already purchased for this Rx
            (or this fill) means a replacement label needs a recorded reason. Scan-check
            reports that verdict up front, so the operator sees the requirement while they
            are still building the package instead of after a rejected create. The reason is
            MANDATORY — canSubmit stays false until it is chosen, so 'Create shipment' is
            simply not available without it. */}
        {relabelPrompt && (
          <div data-testid="reship-reason" style={{ background: '#fef3c7', border: '1px solid #d97706', borderRadius: 12, padding: 12, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <AlertTriangle size={18} style={{ color: '#b45309' }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#b45309', flex: 1 }}>Reship — reason required</span>
              {relabelOrder && (
                <span style={{ fontSize: 11, fontWeight: 600, background: '#fff', color: '#92400e', border: '1px solid #fde68a', padding: '3px 9px', borderRadius: 20 }}>
                  Existing order #{relabelOrder}
                </span>
              )}
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
              {relabelPrompt} Pick a reason to buy another label — this is recorded against the Rx and cannot be skipped.
            </p>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>Reason</label>
            <select data-testid="reship-reason-code" value={relabelCode} onChange={e => setRelabelCode(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d97706', background: '#fff', fontSize: 13, margin: '4px 0 8px' }}>
              <option value="">Select a reason…</option>
              {RELABEL_GROUPS.map(g => (
                <optgroup key={g} label={g}>
                  {RELABEL_REASONS.filter(r => r.group === g).map(r => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {relabelCode === 'EXT99' && (
              <textarea data-testid="reship-reason-text" value={relabelText} onChange={e => setRelabelText(e.target.value)} rows={3}
                placeholder="Required: explain the reason"
                style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d97706', fontSize: 13, marginBottom: 4, boxSizing: 'border-box' }} />
            )}
            {!relabelReady && (
              <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>
                {relabelCode === 'EXT99' ? 'Explain the reason to enable Create shipment.' : 'Choose a reason to enable Create shipment.'}
              </div>
            )}
          </div>
        )}
        {!activeDismissed && activeShipments.length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #d97706', borderRadius: 12, padding: 12, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <AlertTriangle size={18} style={{ color: '#b45309' }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#b45309', flex: 1 }}>{mode === 'facility' ? 'Active package for this facility' : 'Active shipment for this patient'}</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--color-text-mute, #6b7280)', margin: '0 0 10px', lineHeight: 1.5 }}>
              {mode === 'facility'
                ? 'This facility already has a package out today that the courier has not picked up. Any resident\u2019s Rx can go in it. Choose how to handle the new Rx. This list refreshes on its own.'
                : 'This patient already has an order out today that the courier has not picked up. Choose how to handle the new Rx. This list refreshes on its own.'}
            </p>
            {activeShipments.map(sh => (
              <div key={sh.shipment_id} data-testid={`active-order-${sh.shipment_id}`}
                   style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Order {sh.order_number ?? '\u2014'}</span>
                  {sh.combinable === false
                    ? <span style={{ fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#b45309', padding: '3px 9px', borderRadius: 20 }}>Order number pending</span>
                    : <span style={{ fontSize: 11, fontWeight: 600, background: '#eff4ff', color: '#1a56db', padding: '3px 9px', borderRadius: 20 }}>Label purchased</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-mute, #6b7280)', lineHeight: 1.6 }}>
                  <span style={{ color: '#9ca3af' }}>Rx included: </span>
                  {sh.rx.map((x, i) => (
                    <span key={i}>{i > 0 ? ' \u00b7 ' : ''}{x.rx_number}{x.drug_name ? ` \u00b7 ${x.drug_name}` : ''}{mode === 'facility' && x.patient_name ? ` (${x.patient_name})` : ''}</span>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                  {/* Option 1 — Combine into existing order (in-place, same order number).
                      Disabled while the courier order number is still being read back:
                      there is nothing to reprint under yet, and the panel refreshes on
                      its own within a few seconds. */}
                  <button type="button" data-testid="combine-in-place" onClick={() => { void combineInPlace(sh) }}
                    disabled={combiningId === sh.shipment_id || sh.combinable === false}
                    style={{ padding: '9px 12px', fontSize: 13, border: '1px solid var(--color-accent, #1a56db)', background: 'var(--color-accent-subtle, #eff4ff)', color: 'var(--color-accent, #1a56db)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left', cursor: sh.combinable === false ? 'not-allowed' : 'pointer', opacity: sh.combinable === false ? 0.55 : 1 }}>
                    <Plus size={15} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontWeight: 600 }}>{combiningId === sh.shipment_id ? 'Combining…' : 'Combine into existing order'}</span>
                      <span style={{ display: 'block', fontSize: 12, fontWeight: 400, opacity: 0.85 }}>
                        {sh.combinable === false
                          ? 'Waiting for the courier to assign this order a number — this updates automatically.'
                          : 'Add this Rx to the already boxed order and reprint one label under the original order number.'}
                      </span>
                    </span>
                  </button>
                  {/* Option 2 — Cancel and recreate as one order */}
                  <button type="button" onClick={() => { void combineIntoNew(sh) }} disabled={combiningId === sh.shipment_id || sh.combinable === false}
                    style={{ padding: '9px 12px', fontSize: 13, border: '1px solid var(--color-border)', background: '#fff', color: 'var(--color-text)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left', cursor: 'pointer' }}>
                    <XCircle size={15} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontWeight: 600 }}>Cancel and recreate as one order</span>
                      <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--color-text-mute, #6b7280)' }}>Void the original order, create a new order number, and ship both Rx together.</span>
                    </span>
                  </button>
                  {/* Option 3 — Ship separately */}
                  <button type="button" onClick={() => setActiveDismissed(true)}
                    style={{ padding: '9px 12px', fontSize: 13, border: '1px solid var(--color-border)', background: '#fff', color: 'var(--color-text)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left', cursor: 'pointer' }}>
                    <Package size={15} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontWeight: 600 }}>Ship separately</span>
                      <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--color-text-mute, #6b7280)' }}>Leave the boxed order as is and create a new order number for this Rx. Two shipments.</span>
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* mode toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          <ModeBtn active={mode === 'rx'} onClick={() => { setMode('rx'); setQuote(null) }} icon={Search} label="From prescription" />
          <ModeBtn active={mode === 'manual'} onClick={() => { setMode('manual'); setQuote(null) }} icon={Package} label="Manual package" />
          <ModeBtn active={mode === 'facility'} onClick={() => { setMode('facility'); setQuote(null); setEditAddr(false) }} icon={Building2} label="Facility" />
        </div>

        {/* carrier */}
        <label style={labelStyle}>Carrier</label>
        <select value={carrierId} onChange={e => {
          setCarrierId(e.target.value); setQuote(null)
          const c = carriers.find(x => x.id === e.target.value)
          // D-SHIP-23: each carrier has its own services; the list and default reload from it.
          setServiceUserSet(false)
          if (c) { setColdChain(c.default_cold_chain); setRequireSig(c.default_signature) }
        }} style={inputStyle}>
          <option value="">Select a carrier…</option>
          {carriers.map(c => <option key={c.id} value={c.id}>{c.display_name}{c.is_test_mode ? ' (test)' : ''}</option>)}
        </select>

        {pickupsFromCarrier && (
          <div style={{ margin: '10px 0' }}>
            <PickupLocationField carrierId={carrierId} value={pickupLocationId}
              onChange={id => { setPickupChoice({ carrierId, id }); setQuote(null) }} />
          </div>
        )}

        {showService && (<>
        <label style={labelStyle}>Service type</label>
        <select value={serviceType} disabled={services.isLoading} onChange={e => { setServiceType(e.target.value); setServiceUserSet(true); setQuote(null) }}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, marginBottom: 4 }}>
          {services.isLoading && <option value="">Loading services…</option>}
          {serviceOptions.map(o => (
            <option key={o.code} value={o.code} disabled={o.eligible === false}>
              {o.name}{o.price != null ? ` — $${o.price.toFixed(2)}` : ''}{o.eligible === false ? ` (unavailable${o.reason ? `: ${o.reason}` : ''})` : ''}
            </option>
          ))}
        </select>
        <div style={{ fontSize: 11.5, color: services.isError ? '#b91c1c' : 'var(--color-text-muted)', marginBottom: 10, minHeight: 14 }}>
          {services.isError ? "Couldn't load this carrier's services — the account default will be used."
            : services.isFetching ? 'Checking availability…'
            : services.data?.detail ?? (services.data?.address_aware ? 'Availability and price for this address.' : '')}
        </div>
        </>)}
        {serviceType === 'SATURDAY' && isFridayLocal() && !serviceUserSet && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-accent, #1a56db)', marginBottom: 10 }}>
            <Truck size={13} />
            <span>It's Friday — defaulted to Saturday delivery. Change the dropdown if you don't want it.</span>
          </div>
        )}

        {/* D-SHIP-16: print-on-create toggle (default ON) */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', border: '1px solid var(--color-border)', borderRadius: 8, cursor: 'pointer', marginBottom: 10, background: printAfterCreate ? 'var(--color-accent-subtle, #eff4ff)' : 'transparent' }}>
          <input type="checkbox" checked={printAfterCreate} onChange={e => setPrintAfterCreate(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>Print label after creating</span>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-mute, #6b7280)' }}>
              {printAfterCreate ? 'The label prints as soon as the order is confirmed.' : 'Creates without printing.'}
            </span>
          </span>
          <Printer size={16} style={{ color: 'var(--color-text-mute, #6b7280)' }} />
        </label>

        {/* Rx picker */}
        {isRxMode && (
          <div style={{ marginTop: 12 }}>
            {/* D-SHIP-FAC-1: facility package — the destination is the FACILITY the primary Rx ships to.
                Residents are listed per Rx below; the patient address block is not shown. */}
            {mode === 'facility' && !selectedRx && (
              <div style={{ marginBottom: 8, padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg-soft, #fafafa)', fontSize: 12.5, color: 'var(--color-text-muted)' }}>
                One package to one facility address. Pick or scan the first Rx; every Rx scanned after it must ship to the <strong>same facility</strong> — any resident of that facility is allowed.
              </div>
            )}
            {mode === 'facility' && selectedRx && facSocErr && (
              <div role="alert" style={{ marginTop: 8, padding: 10, borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', fontSize: 12.5, color: '#92400e' }}>Couldn't resolve the facility: {facSocErr}</div>
            )}
            {mode === 'facility' && selectedRx && facSoc && !facSoc.facility_id && (
              <div role="alert" style={{ marginTop: 8, padding: 10, borderRadius: 8, border: '2px solid #dc2626', background: '#fef2f2', fontSize: 12.5, color: '#991b1b' }}>
                <strong>Not a facility Rx.</strong> Rx {selectedRx.rx_number} ships to the patient's home address. Choose a different Rx, or use <em>From prescription</em>.
              </div>
            )}
            {mode === 'facility' && selectedRx && facSoc?.facility_id && (
              <div style={{ marginTop: 8, padding: 10, borderRadius: 8, border: '1px solid #0e7490', background: '#ecfeff', fontSize: 12.5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: .5, padding: '2px 7px', borderRadius: 4, background: '#0e7490', color: '#fff' }}>{(facSoc.facility_type_label || facSoc.facility_type).toUpperCase()}</span>
                  <strong>{facSoc.facility_name}</strong>
                </div>
                <div style={{ color: 'var(--color-text-muted)', marginTop: 3 }}>
                  {facSoc.facility_address || ''}
                  {facSoc.contact_phone || facSoc.facility_phone ? ` · ${formatPhone(facSoc.contact_phone || facSoc.facility_phone || '')}` : ''}
                  {facSoc.contact_name ? ` · Attn ${facSoc.contact_name}` : ''}
                  {facSoc.delivery_cutoff_time ? ` · Cutoff ${facSoc.delivery_cutoff_time}` : ''}
                </div>
              </div>
            )}
            {/* Patient shipping context: full identity + destination address */}
            {mode === 'rx' && selectedRx && !ctx && ctxErr && (
              <div style={{ marginTop: 8, padding: 10, borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', fontSize: 12.5, color: '#92400e' }}>
                Couldn't load patient info: {ctxErr}
              </div>
            )}
            {mode === 'rx' && selectedRx && ctx && (
              <div style={{ marginTop: 8, padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg-soft, #fafafa)', fontSize: 12.5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong>{ctx.patient.display_name || 'Patient'}</strong>
                  {!editAddr && <button type="button" style={linkBtn} onClick={() => setEditAddr(true)}>edit address</button>}
                </div>
                <div style={{ color: 'var(--color-text-muted)', marginTop: 2 }}>
                  {ctx.patient.date_of_birth ? `DOB ${formatDob(ctx.patient.date_of_birth)} · ` : ''}
                  {ctx.patient.primary_phone ? `${formatPhone(ctx.patient.primary_phone)} · ` : ''}
                  {ctx.patient.email || ''}
                </div>
                {!editAddr ? (
                  <div style={{ marginTop: 4 }}>
                    {/* ASP-92 reopen: `addr.line1 || fallback` RENDERED the line, then the
                        next expression rendered it again → "512 Zinnia St512 Zinnia St".
                        The fallback must render only when the line is absent. */}
                    {!addr.line1 && <span style={{ color: '#b45309' }}>No address on file — add one before shipping.</span>}
                    {addr.line1 && <>{addr.line1}, {addr.city}{addr.state ? `, ${addr.state}` : ''} {addr.zip}</>}
                    {(ctx.addresses?.length ?? 0) > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {ctx.addresses.map(a => (
                          <button key={a.id} type="button" title={`${a.line1}, ${a.city} ${a.postal_code}`}
                            onClick={() => { setAddr({ line1: a.line1, city: a.city, state: a.state_code ?? '', zip: a.postal_code, phone: a.phone ?? '' }); setEditAddr(true); setAddToList(false) }}
                            style={{ fontSize: 11, padding: '3px 8px', borderRadius: 12, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer' }}>
                            📍 {a.label || a.line1}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 6 }}>
                    <input value={addr.line1} onChange={e => setAddr(a => ({ ...a, line1: e.target.value }))} maxLength={256} placeholder="Street address" style={inputStyle} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input value={addr.city} onChange={e => setAddr(a => ({ ...a, city: e.target.value }))} maxLength={128} placeholder="City" style={{ ...inputStyle, flex: 2 }} />
                      <input value={addr.state} onChange={e => setAddr(a => ({ ...a, state: e.target.value }))} maxLength={2} placeholder="ST" style={{ ...inputStyle, flex: 1 }} />
                      <input value={addr.zip} onChange={e => setAddr(a => ({ ...a, zip: e.target.value }))} maxLength={10} placeholder="ZIP" style={{ ...inputStyle, flex: 1 }} />
                    </div>
                    <input value={addr.phone} onChange={e => setAddr(a => ({ ...a, phone: e.target.value }))} maxLength={32} placeholder="Phone (optional)" style={inputStyle} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 4, cursor: 'pointer' }}>
                      <input type="radio" checked={!addToList} onChange={() => setAddToList(false)} />
                      One-time edit (this shipment only)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 2, cursor: 'pointer' }}>
                      <input type="radio" checked={addToList} onChange={() => setAddToList(true)} />
                      Add to the patient's address list
                    </label>
                    {addToList && (
                      <input value={addrLabel} onChange={e => setAddrLabel(e.target.value)} maxLength={60}
                        placeholder="Label (optional — e.g. Work, Daughter's house)" style={{ ...inputStyle, marginTop: 4 }} />
                    )}
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                      The primary address comes from PioneerRx and can't be changed here — update it in PioneerRx.
                    </div>
                    <button type="button" style={{ ...linkBtn, marginTop: 4 }}
                      onClick={() => { setEditAddr(false); if (ctx) setAddr({ line1: ctx.patient.address_line ?? '', city: ctx.patient.city ?? '', state: ctx.patient.state_code ?? '', zip: ctx.patient.postal_code ?? '', phone: ctx.patient.primary_phone ?? '' }); setAddToList(false); setAddrLabel('') }}>
                      cancel edit
                    </button>
                  </div>
                )}
              </div>
            )}

            <label style={labelStyle}>{selectedRx ? `Prescriptions in this package (${1 + pkgItems.length})` : 'Prescription'}</label>
            {selectedRx ? (
              <div>
                {/* D-SHIP-COPAY-MULTI (Patrick 2026-08-05): all Rx in one group — primary first
                    (moved down out of its own header), then each scanned member, each with its
                    copay + collected/pending chip. */}
                {(() => {
                  const members = [
                    { key: 'primary', rx: (ctx?.rx.rx_number || selectedRx.rx_number || 'Rx'), drug: (ctx?.rx.drug_name || selectedRx.drug_name || ''), amt: ctx?.copay ?? null, collected: ctx?.copay_collected === true, primary: true, override: false, patient: selectedRx.patient_name ?? null },
                    ...pkgItems.map(i => ({ key: i.rx_item_id, rx: i.rx_number, drug: i.drug_name || '', amt: i.copay ?? null, collected: i.collected === true, primary: false, override: !!i.mismatch_override, patient: i.patient_name ?? null })),
                  ]
                  return members.map(m => (
                    <div key={m.key} style={{ ...selectedBox, marginTop: 4, alignItems: 'flex-start' }}>
                      <div>
                        <strong>{m.rx}</strong>{m.drug ? <> — {m.drug}</> : null}
                        {m.override && <span style={{ marginLeft: 6, fontSize: 10, color: '#b45309', fontWeight: 700 }}>OVERRIDE</span>}
                        {m.primary && (
                          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                            {selectedRx.patient_name ? `${selectedRx.patient_name} · ` : ''}state: {selectedRx.state}
                          </div>
                        )}
                        {!m.primary && mode === 'facility' && (
                          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{m.patient || 'Resident'}</div>
                        )}
                        {m.amt != null && m.amt > 0 && (
                          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>${m.amt.toFixed(2)}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap', color: m.collected ? '#1e7a46' : '#b8690f', background: m.collected ? '#e6f4ec' : '#fdf1e3' }}>{m.collected ? '✓ Collected' : '⏳ Pending'}</span>
                          </div>
                        )}
                      </div>
                      {m.primary
                        ? <button type="button" onClick={() => { setSelectedRx(null); setPkgItems([]); setScanQ(''); setScanNote(null); setQuote(null) }} style={linkBtn}>change</button>
                        : <button type="button" style={linkBtn} onClick={() => setPkgItems(items => items.filter(x => x.rx_item_id !== m.key))}>remove</button>}
                    </div>
                  ))
                })()}
                {/* D-SHIP-1: scan more Rx into this package */}
                <input
                  ref={scanInputRef}
                  value={scanQ}
                  onChange={e => { setScanQ(e.target.value); setScanNote(null) }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void onScan() } }}
                  placeholder="Scan or type another Rx number, then Enter…"
                  style={{ ...inputStyle, marginTop: 6 }}
                  autoFocus
                />
                {scanSug.length > 0 && (
                  <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 2, overflow: 'hidden' }}>
                    {scanSug.map(r => (
                      <button key={r.id} type="button"
                        onClick={() => { if (r.rx_number) void onScan(r.rx_number) }}
                        style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', fontSize: 12, textAlign: 'left' }}>
                        <span><strong>{r.rx_number}</strong>{r.drug_name ? ` — ${r.drug_name}` : ''}{r.patient_name ? ` · ${r.patient_name}` : ''}</span>
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{r.state}</span>
                      </button>
                    ))}
                  </div>
                )}
                {scanNote && <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>{scanNote}</div>}
                {/* copay total bar — exactly what the driver collects (pending only) */}
                {(() => {
                  const cr = [
                    { amt: ctx?.copay ?? 0, collected: ctx?.copay_collected === true },
                    ...pkgItems.map(i => ({ amt: i.copay ?? 0, collected: i.collected === true })),
                  ].filter(r => r.amt > 0)
                  if (cr.length === 0) {
                    return copayKnown ? (
                      <div style={{ marginTop: 10, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 12.5, color: 'var(--color-text-mute, #6b7280)' }}>
                        💵 No copay — nothing to collect (per latest claim)
                      </div>
                    ) : null
                  }
                  // SETTLEMENT IS BINARY (2026-08-05). `collected` means this fill's sale is
                  // closed in PioneerRx — there is no partial-payment signal — so an unsettled
                  // member owes its copay in full and a settled one owes nothing. The banner and
                  // the per-Rx chips therefore read from the same flag the printed label uses,
                  // which is the point: staff and courier must never see different answers.
                  const total = cr.reduce((sum, r) => sum + r.amt, 0)
                  const collectSum = cr.filter(r => !r.collected).reduce((sum, r) => sum + r.amt, 0)
                  const allCollected = cr.every(r => r.collected)
                  const collectedN = cr.filter(r => r.collected).length
                  const pendingN = cr.length - collectedN
                  const accentC = allCollected ? '#059669' : '#d97706'
                  const textC = allCollected ? '#065f46' : '#92400e'
                  return (
                    <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${accentC}`, background: allCollected ? '#ecfdf5' : '#fffbeb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, gap: 8 }}>
                      <span style={{ fontWeight: 700, color: textC }}>Copay total ${total.toFixed(2)}{cr.length > 1 ? ` · ${collectedN} collected · ${pendingN} pending` : ''}</span>
                      <span style={{ fontWeight: 700, color: textC, whiteSpace: 'nowrap' }}>{allCollected ? '✓ All collected' : `⚠ Driver collects $${collectSum.toFixed(2)}`}</span>
                    </div>
                  )
                })()}
              </div>
            ) : (
              <>
                <input ref={rxInputRef} value={rxQuery} onChange={e => setRxQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void resolveScan(rxQuery) } }}
                  placeholder="Scan a label, or search by Rx number or patient…" style={inputStyle} />
                {rxSearching && <div style={{ ...mutedText, marginTop: 4 }}>Searching…</div>}
                {rxResults.length > 0 && (
                  <div style={resultsList}>
                    {rxResults.map(r => (
                      <button key={r.id} type="button" style={resultRow}
                        onClick={() => { setSelectedRx(r); setRxResults([]); setRxQuery('') }}>
                        <span><strong>{r.rx_number || 'Rx'}</strong> — {r.drug_name || ''}</span>
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{r.state}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ ...mutedText, marginTop: 4, fontSize: 11 }}>
                  Any prescription can be selected — a label can be created before dispensing is complete.
                </div>
              </>
            )}


            {/* ASP-95 (Patrick): delivery notes from the patient profile's delivery
                section, shown next to copay. Display-only — never auto-flows to the
                courier note (Sasha ruling); the button appends it deliberately. */}
            {ctx?.patient?.delivery_notes && (
              <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg-subtle, #f9fafb)', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>🚚 <b>Delivery notes:</b> {ctx.patient.delivery_notes}</span>
                <button type="button"
                  onClick={() => setInstructions(prev => {
                    const t = ctx.patient.delivery_notes!.trim()
                    return prev.includes(t) ? prev : (prev ? `${prev}; ${t}` : t)
                  })}
                  style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11.5 }}>
                  Add to instructions
                </button>
              </div>
            )}





            {/* Patient-mismatch warning (soft-block + audited override) */}
            {mismatch && (
              <div style={{ marginTop: 8, padding: 12, borderRadius: 8, border: '2px solid #f59e0b', background: '#fffbeb' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                  {mismatch.mismatch_kind === 'address'
                    ? '⚠ Address mismatch — a facility package ships to one facility address'
                    : "⚠ Patient mismatch — a shipment can only contain one patient's Rx"}
                  {mismatchQueue.length > 0 && <span style={{ fontWeight: 500 }}> ({mismatchQueue.length} more to review)</span>}
                </div>
                <div style={{ fontSize: 12.5, marginTop: 4, color: '#78350f' }}>
                  {mismatch.mismatch_kind === 'address' ? (
                    <>Rx <strong>{mismatch.rx_number}</strong>{mismatch.drug_name ? <> ({mismatch.drug_name})</> : null} for <strong>{mismatch.actual_patient_name || 'this patient'}</strong> ships to{' '}
                      <strong>{mismatch.actual_facility_name || "the patient's home"}</strong> — this package goes to <strong>{mismatch.expected_facility_name || 'a different facility'}</strong>.</>
                  ) : (
                    <>Rx <strong>{mismatch.rx_number}</strong>{mismatch.drug_name ? <> ({mismatch.drug_name})</> : null} belongs to{' '}
                      <strong>{mismatch.actual_patient_name || 'a different patient'}</strong> — not the selected patient for this package.</>
                  )}
                </div>
                <input value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                  placeholder="Reason to ship anyway (required to override)…"
                  style={{ ...inputStyle, marginTop: 8 }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" style={{ ...linkBtn, color: '#b45309', fontWeight: 700 }}
                    disabled={!overrideReason.trim()}
                    onClick={() => {
                      setPkgItems(items => [...items, { rx_item_id: mismatch.rx_item_id, rx_number: mismatch.rx_number, drug_name: mismatch.drug_name, patient_name: mismatch.actual_patient_name, mismatch_override: true, override_reason: overrideReason.trim() }])
                      setMismatch(null); setOverrideReason(''); setScanQ('')
                    }}>
                    Override and add
                  </button>
                  <button type="button" style={linkBtn} onClick={() => { setMismatch(null); setOverrideReason(''); setScanQ('') }}>
                    Don't add
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manual: pickup + drop-off (D-SHIP-PU) */}
        {mode === 'manual' && (
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Pickup location</label>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              {(['pharmacy', 'patient', 'other'] as const).map(t => (
                <button key={t} type="button" onClick={() => setPickupType(t)}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 8, cursor: 'pointer',
                    border: pickupType === t ? '2px solid var(--color-primary, #0e7c7b)' : '1px solid var(--color-border, #d1d5db)',
                    background: pickupType === t ? 'var(--color-primary-soft, #e6f4f4)' : 'transparent',
                    fontWeight: pickupType === t ? 600 : 400, textTransform: 'capitalize' }}>
                  {t}
                </button>
              ))}
            </div>
            {pickupType === 'pharmacy' ? (
              pharmacyAddrLine
                ? <div style={{ fontSize: 12, color: 'var(--color-text-muted, #6b7280)', marginBottom: 8 }}>Pickup at: {pharmacyAddrLine}</div>
                : <div style={{ fontSize: 12, color: 'var(--color-danger, #b91c1c)', marginBottom: 8 }}>No pharmacy address configured — set it under My Pharmacy &gt; Profile, or choose Patient/Other.</div>
            ) : (
              <div style={{ marginBottom: 8 }}>
                {pickupType === 'patient' && (
                  <div style={{ marginBottom: 6, position: 'relative' }}>
                    <label style={labelStyle}>Search patient</label>
                    <input value={puPatQ} onChange={e => searchPatients(e.target.value, 'pickup')}
                      placeholder="Name, DOB, or phone" style={inputStyle} />
                    {puPatResults.length > 0 && (
                      <div style={{ border: '1px solid var(--color-border, #d1d5db)', borderRadius: 8, marginTop: 2, background: 'var(--color-surface, #fff)' }}>
                        {puPatResults.map(r => (
                          <div key={r.id} onClick={() => pickPatient(r, 'pickup')}
                            style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--color-border, #eee)' }}>
                            {r.display_name}{r.date_of_birth ? ` — DOB ${r.date_of_birth}` : ''}{r.city ? ` — ${r.city}, ${r.state_code || ''}` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {puPatSel && <div style={{ fontSize: 12, color: 'var(--color-success, #15803d)', marginTop: 2 }}>Address filled from {puPatSel.name}'s record — verify below.</div>}
                  </div>
                )}
                <label style={labelStyle}>{pickupType === 'patient' ? 'Patient name (pickup)' : 'Pickup contact name'}</label>
                <input value={puName} onChange={e => setPuName(e.target.value)} style={inputStyle} />
                <label style={labelStyle}>Pickup street address</label>
                <input value={puLine1} onChange={e => setPuLine1(e.target.value)} style={inputStyle} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>City</label>
                    <input value={puCity} onChange={e => setPuCity(e.target.value)} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>State</label>
                    <input value={puState} onChange={e => setPuState(e.target.value)} maxLength={2} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>ZIP</label>
                    <input value={puZip} onChange={e => setPuZip(e.target.value)} style={inputStyle} />
                  </div>
                </div>
                <label style={labelStyle}>Pickup phone</label>
                <input value={puPhone} onChange={e => setPuPhone(e.target.value)} style={inputStyle} />
              </div>
            )}

            <label style={labelStyle}>Drop-off location</label>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              {(['patient', 'pharmacy', 'other'] as const).map(t => (
                <button key={t} type="button" onClick={() => setDropType(t)}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 8, cursor: 'pointer',
                    border: dropType === t ? '2px solid var(--color-primary, #0e7c7b)' : '1px solid var(--color-border, #d1d5db)',
                    background: dropType === t ? 'var(--color-primary-soft, #e6f4f4)' : 'transparent',
                    fontWeight: dropType === t ? 600 : 400, textTransform: 'capitalize' }}>
                  {t}
                </button>
              ))}
            </div>
            {dropType === 'pharmacy' ? (
              pharmacyAddrLine
                ? <div style={{ fontSize: 12, color: 'var(--color-text-muted, #6b7280)', marginBottom: 8 }}>Deliver to: {pharmacyAddrLine}</div>
                : <div style={{ fontSize: 12, color: 'var(--color-danger, #b91c1c)', marginBottom: 8 }}>No pharmacy address configured — set it under My Pharmacy &gt; Profile, or choose Patient/Other.</div>
            ) : (
              <div>
                {dropType === 'patient' && (
                  <div style={{ marginBottom: 6, position: 'relative' }}>
                    <label style={labelStyle}>Search patient</label>
                    <input value={dropPatQ} onChange={e => searchPatients(e.target.value, 'drop')}
                      placeholder="Name, DOB, or phone" style={inputStyle} />
                    {dropPatResults.length > 0 && (
                      <div style={{ border: '1px solid var(--color-border, #d1d5db)', borderRadius: 8, marginTop: 2, background: 'var(--color-surface, #fff)' }}>
                        {dropPatResults.map(r => (
                          <div key={r.id} onClick={() => pickPatient(r, 'drop')}
                            style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--color-border, #eee)' }}>
                            {r.display_name}{r.date_of_birth ? ` — DOB ${r.date_of_birth}` : ''}{r.city ? ` — ${r.city}, ${r.state_code || ''}` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {dropPatSel && <div style={{ fontSize: 12, color: 'var(--color-success, #15803d)', marginTop: 2 }}>Address filled from {dropPatSel.name}'s record — verify below.</div>}
                  </div>
                )}
                {/* D-SHIP-COD-MANUAL. Only on manual packages: an Rx order's COD is DERIVED from
                    claims, and overriding that by hand is a different feature that already exists
                    (manager copay override). Hidden without copay.override — the server enforces
                    the same rule, this only avoids showing a control that would be rejected. */}
                {canSetCod && (
                  <>
                    <label style={labelStyle}>Collect on delivery (optional)</label>
                    <input value={codAmount} onChange={e => setCodAmount(e.target.value)}
                      type="number" min="0" max="10000" step="0.01" placeholder="0.00"
                      style={inputStyle}
                      title="Amount the driver collects at the door. Leave blank for no COD." />
                    <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginTop: -4, marginBottom: 8 }}>
                      Printed on the label and sent to the courier to collect. Leave blank for no COD.
                    </div>
                  </>
                )}
                <label style={labelStyle}>{dropType === 'patient' ? 'Patient name (recipient)' : 'Recipient name'}</label>
                <input value={recipient} onChange={e => setRecipient(e.target.value)} maxLength={128} style={inputStyle} />
                <label style={labelStyle}>Street address</label>
                <input value={line1} onChange={e => setLine1(e.target.value)} maxLength={256} style={inputStyle} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>City</label>
                    <input value={city} onChange={e => setCity(e.target.value)} maxLength={128} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>State</label>
                    <input value={stateCode} onChange={e => setStateCode(e.target.value)} maxLength={2} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>ZIP</label>
                    <input value={zip} onChange={e => setZip(e.target.value)} maxLength={16} style={inputStyle} />
                  </div>
                </div>
                <label style={labelStyle}>Phone</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} maxLength={32} style={inputStyle} />
              </div>
            )}
            <label style={labelStyle}>Package description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Supplies" maxLength={256} style={inputStyle} />
          </div>
        )}

        {/* handling */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '12px 0' }}>
          <Toggle checked={coldChain} onChange={setColdChain} icon={Snowflake} label="Cold chain (temperature-controlled)" />
          <Toggle checked={requireSig} onChange={setRequireSig} icon={PenLine} label="Signature required" />
          {sigAutoNote && requireSig && (
            <div style={{ fontSize: 11, color: 'var(--color-warning, #a16207)', margin: '-2px 0 2px 26px' }}>
              Auto-suggested: high-value medication. You can uncheck if not needed.
            </div>
          )}
          <Toggle checked={adultSig} onChange={setAdultSig} icon={PenLine} label="Adult signature required" />
          <Toggle checked={specialHandling} onChange={setSpecialHandling} icon={AlertTriangle} label="Special handling" />
        </div>

        <label style={labelStyle}>Special instructions</label>
        {/* ASP-88 (Patrick): quick-text multi-select — checked phrases append into the
            free-text box below, which stays fully editable. Presets are per-pharmacy
            (Settings → Shipping labels). */}
        {presets.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', margin: '2px 0 4px' }}>
            {presets.map(t => {
              const parts = instructions.split(/\s*;\s*/).map(x => x.trim())
              const checked = parts.includes(t)
              return (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked} onChange={() => togglePreset(t)} />
                  {t}
                </label>
              )
            })}
          </div>
        )}
        <textarea value={instructions} onChange={e => setInstructions(e.target.value)} rows={2} maxLength={INSTRUCTIONS_MAX} style={{ ...inputStyle, resize: 'vertical' }} />
        <div style={{ fontSize: 11.5, textAlign: 'right', marginTop: 2, color: instructionsTooLong ? '#b91c1c' : 'var(--color-text-muted)' }}>
          {instructionsTooLong ? `Too long for the courier — shorten to ${INSTRUCTIONS_MAX} characters. ` : ''}{instructions.length} / {INSTRUCTIONS_MAX}
        </div>

        {liveQuote && (
          <div style={quoteBox}>Rate: <strong>{liveQuote.amount != null ? `$${liveQuote.amount.toFixed(2)}` : 'n/a'}</strong>
            <span style={{ color: 'var(--color-text-muted)', marginLeft: 6 }}>{liveQuote.detail}</span></div>
        )}
        {quote && !liveQuote && (
          <div style={{ ...quoteBox, color: 'var(--color-text-muted)' }}>The order changed since the last rate — get a new rate.</div>
        )}
        {error && <div style={errorBox}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={secondaryBtn} disabled={busy}>Cancel</button>
          {requireQuote && !liveQuote ? (
            <button type="button" onClick={getQuote} style={primaryBtn} disabled={!canSubmit || busy}>{busy ? 'Getting rate…' : 'Get rate'}</button>
          ) : (
            <button type="button" onClick={submit} style={primaryBtn} disabled={!canSubmit || busy}>{busy ? 'Creating…' : liveQuote ? 'Confirm & create' : 'Create shipment'}</button>
          )}
        </div>
      </div>
    </div>
  )
}

function ModeBtn({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: any; label: string }) {
  return (
    <button type="button" onClick={onClick} style={{ ...modeBtn, ...(active ? modeBtnActive : {}) }}>
      <Icon size={14} /> {label}
    </button>
  )
}
function Toggle({ checked, onChange, icon: Icon, label }: { checked: boolean; onChange: (v: boolean) => void; icon: any; label: string }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <Icon size={13} /> {label}
    </label>
  )
}

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 }
const drawer: React.CSSProperties = { width: 'min(460px, 100%)', height: '100%', background: 'var(--color-surface)', padding: 20, overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,0.15)' }
// ASP-61 full-screen: a real page surface — NOT the modal overlay (no dark backdrop, no
// click-outside-to-close, not pinned right). Opened as its own tab, so dismissal is explicit.
const fullPage: React.CSSProperties = { position: 'fixed', inset: 0, background: 'var(--color-surface)', overflowY: 'auto', zIndex: 1000, padding: 0 }   // D-SHIP-9: edge-to-edge
const drawerFull: React.CSSProperties = { width: '100%', maxWidth: 'none', minHeight: '100%', margin: 0, background: 'var(--color-surface)', borderRadius: 0, border: 'none', padding: '24px clamp(24px, 5vw, 64px)', boxSizing: 'border-box' }   // D-SHIP-9: Full screen means the ENTIRE screen
const modeBtn: React.CSSProperties = { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text)' }
const modeBtnActive: React.CSSProperties = { background: 'var(--color-primary, #0369a1)', color: '#fff', borderColor: 'var(--color-primary, #0369a1)' }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 8 }
const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 6, boxSizing: 'border-box' }
const selectedBox: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 10, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-bg, #f8fafc)', fontSize: 13 }
const resultsList: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 4, overflow: 'hidden' }
const resultRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', width: '100%', padding: '8px 10px', fontSize: 13, background: 'var(--color-surface)', border: 'none', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', textAlign: 'left' }
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--color-primary, #0369a1)', fontSize: 12, cursor: 'pointer' }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-primary, #0369a1)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }
const secondaryBtn: React.CSSProperties = { background: 'transparent', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }
const quoteBox: React.CSSProperties = { marginTop: 12, padding: 10, background: 'var(--color-bg, #f8fafc)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13 }
const errorBox: React.CSSProperties = { marginTop: 10, padding: 8, background: '#dc26260d', border: '1px solid #dc262633', borderRadius: 6, fontSize: 12, color: '#dc2626' }
const mutedText: React.CSSProperties = { color: 'var(--color-text-muted)', fontSize: 13 }
